import { jsx, jsxs } from 'react/jsx-runtime';
import classNames from 'classnames';
import { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { findLocaleFromUrlAndCookie } from '@zooplus/commons-lang';
import { AlertCircle, CheckCircle, Cross } from '@zooplus/icons';
import { ShadowBox, P1, Alert, FlatBox, AutoshipmentLogoLockup, P2, ButtonAnchor, CartButton } from '@zooplus/ui-components';
import { useTranslation } from '../../hooks/useTranslation.js';
import { sendAdobeData } from '../../services/adobeService.js';
import styles from './Popover.module.scss.js';

const StatusTranslations = {
    ADDED_TO_MAX_QUANTITY: 'shop.add-to-cart.popover.message.warning.stock',
    ALREADY_MAX_QUANTITY: 'shop.add-to-cart.popover.message.warning.stock',
    UNCHANGED: 'shop.add-to-cart.popover.message.success',
    ADDED: 'shop.add-to-cart.popover.message.success',
    REDUCED: 'shop.add-to-cart.popover.message.reduced',
    DELETED: 'shop.add-to-cart.popover.message.deleted',
};
const ErrorCodeTranslations = {
    ARTICLE_NOT_FOUND: 'shop.add-to-cart.popover.message.error',
    CART_NOT_FOUND: 'shop.add-to-cart.popover.message.error',
    INVALID_SID: 'shop.add-to-cart.popover.message.error',
    INCORRECT_JSON_BODY: 'shop.add-to-cart.popover.message.error',
    NOT_ENOUGH_ARTICLES: 'shop.add-to-cart.popover.message.error',
};
const CLOSE_TIMEOUT = 6000;
const ARROW_HEIGHT = 15;
const POPOVER_TITLE_ID = 'addToCartPopoverTitle';
const abdNotApplied = 'SHOP_CHECKOUT_ABV_NOT_APPLIED';
const cartLimitExceeded = 'SHOP_CHECKOUT_CART_ITEMS_LIMIT';
const getCartText = (quantity, t) => {
    switch (quantity) {
        case 0:
            return t('shop.add-to-cart.popover.empty.cart');
        case 1:
            return t('shop.add-to-cart.popover.cart.text.singular');
        default:
            return t('shop.add-to-cart.popover.cart.text.plural', { quantity });
    }
};
const computePosition = (info, box) => {
    const popoverHeight = box?.offsetHeight || 0;
    const triggerHeight = info.popover.trigger.offsetHeight;
    const rect = info.popover.trigger.getBoundingClientRect();
    const triggerTopInPage = rect.top + window.pageYOffset;
    const displayPopoverAboveTrigger = rect.top > popoverHeight + ARROW_HEIGHT;
    return {
        top: displayPopoverAboveTrigger
            ? triggerTopInPage - popoverHeight - ARROW_HEIGHT
            : triggerTopInPage + triggerHeight + ARROW_HEIGHT,
        right: window.innerWidth - rect.right,
        arrow: displayPopoverAboveTrigger ? 'above' : 'below',
    };
};
const Popover = ({ minOrderValue, actionInfo, realm, proceedToCheckoutFlag, debugMode, cart, }) => {
    const { ready, t } = useTranslation();
    const locale = findLocaleFromUrlAndCookie();
    // Whether or not the popover should be shown
    const [show, setShow] = useState(false);
    // Whether or not the popover should be visible
    // The popover will be effetively visible if show==true & visible==true
    // This visible state is necessary for beeing able to add the popover
    // to the dom in a first step and compute its position, which involves its height, in a second step.
    const [visible, setVisible] = useState(false);
    const boxRef = useRef(null);
    const [info, setInfo] = useState();
    const hideTimeoutRef = useRef();
    const mountedRef = useRef(false);
    useEffect(() => {
        // executed when component is mounted
        mountedRef.current = true;
        return () => {
            // executed when unmount
            mountedRef.current = false;
        };
    }, []);
    useEffect(() => {
        if (actionInfo && actionInfo !== info) {
            !show && setVisible(false);
            setShow(true);
            setInfo(actionInfo);
            if (!debugMode) {
                if (hideTimeoutRef.current) {
                    clearTimeout(hideTimeoutRef.current);
                }
                hideTimeoutRef.current = setTimeout(() => {
                    if (mountedRef.current) {
                        setShow(false);
                    }
                }, CLOSE_TIMEOUT);
            }
        }
    }, [actionInfo, info, show, debugMode]);
    useEffect(() => {
        if (show) {
            setVisible(true);
        }
    }, [show]);
    if (!cart || !info || !show || !ready) {
        return null;
    }
    const { error } = info;
    const subTotal = cart.summary?.subTotal || 0;
    const isAboveMinOrderValue = subTotal > minOrderValue;
    const totalPrice = cart.summary?.formattedSubtotal || 0;
    const cartText = getCartText(cart.summary?.articleCount || 0, t);
    const isDoublePricing = !!cart.summary?.doublePricingSubTotal;
    const doublePricingLabels = cart.summary?.doublePricingSubTotal?.labels;
    let title;
    if (error) {
        title =
            error.code && Object.keys(ErrorCodeTranslations).includes(error.code)
                ? t(ErrorCodeTranslations[error.code], { quantity: info.quantity })
                : error.message;
    }
    else {
        title = info.status ? t(StatusTranslations[info.status], { quantity: info.quantity }) : '';
    }
    const voucherNotAppliedMessageObj = cart?.cartMessages?.find((m) => m.code === abdNotApplied);
    const voucherNotAppliedMessageKey = voucherNotAppliedMessageObj?.messageKey;
    const voucherNotAppliedMessage = voucherNotAppliedMessageKey && t(voucherNotAppliedMessageKey);
    const maxCartExceededMessage = cart?.cartMessages?.find((m) => m.code === cartLimitExceeded);
    const position = computePosition(info, boxRef.current || undefined);
    const showSubscriptionMessage = cart?.articles?.some((article) => article.subscriptionApplied ||
        article.lineItemDiscounts?.autoshipmentDiscount?.applied);
    function onCloseClick() {
        setShow(false);
    }
    return createPortal(jsx("div", { role: "dialog", "aria-labelledby": POPOVER_TITLE_ID, "data-zta": "popoverWrapper", className: classNames(styles.Popover, { 'has-error': info.error }, actionInfo.popover.className), style: {
            top: position.top,
            right: position.right,
            visibility: visible ? 'visible' : 'hidden',
            ...(actionInfo.popover?.style || {}),
        }, ref: boxRef, children: jsx(ShadowBox, { children: jsxs("div", { "data-zta": "popoverInnerWrapper", className: classNames(styles.InnerWrapper, position.arrow, { 'has-error': info.error }), children: [jsxs("div", { "data-zta": "popoverHeader", className: classNames(styles.Header, { 'has-error': info.error }), children: [jsxs("div", { className: styles.TitleWrapper, children: [info.error && (jsx(AlertCircle, { size: 20, "data-zta": "addToCartPopoverErrorIcon" })), !info.error && (jsx(CheckCircle, { size: 20, "data-zta": "addToCartPopoverSuccessIcon" })), jsx(P1, { bold: true, className: styles.Title, "data-zta": POPOVER_TITLE_ID, children: title })] }), jsxs("button", { "data-zta": "addToCartPopoverClose", type: "button", className: styles.Close, onClick: onCloseClick, children: [jsx("span", { className: styles.AccessibleText, children: t('shop.add-to-cart.popover.close') }), jsx(Cross, { size: 20 })] })] }), jsxs("div", { className: styles.Body, children: [voucherNotAppliedMessage && (jsx("div", { className: styles.MessageWrapper, "data-zta": "addToCartPopoverMessage", children: jsx(Alert, { variant: "info", children: voucherNotAppliedMessage }) })), maxCartExceededMessage?.messageKey && maxCartExceededMessage?.messageParams && (jsx("div", { className: styles.MessageWrapper, "data-zta": "addToCartPopoverMessage", children: jsx(Alert, { variant: "info", children: t(maxCartExceededMessage.messageKey, {
                                        0: maxCartExceededMessage.messageParams[0],
                                    }) }) })), showSubscriptionMessage && (jsxs(FlatBox, { rounded: true, className: styles.SubscriptionMessageWrapper, children: [jsx(AutoshipmentLogoLockup, { locale: locale, realm: realm }), jsx(P2, { children: t('shop.addtocart.popover.subscription.message') })] })), jsxs("div", { className: styles.BodyInfo, children: [jsx("p", { "data-zta": "addToCartPopoverText", children: cartText }), subTotal > 0 && !isDoublePricing && (jsx("p", { "data-zta": "addToCartPopoverTotalPrice", className: styles.TotalPrice, children: totalPrice })), subTotal > 0 && isDoublePricing && (jsxs("div", { className: styles.DoublePricingWrapper, children: [jsx(P1, { bold: true, "data-zta": "addToCartPopoverTotalPrice", className: styles.TotalPrice, children: doublePricingLabels?.localCurrency }), jsxs(P2, { bold: true, className: styles.Secondary, children: ["(", doublePricingLabels?.secondaryCurrency, ")"] })] }))] })] }), jsxs("div", { className: realm === 'zooplus' ? classNames(styles.Footer, styles.Zooplus) : classNames(styles.Footer, styles.Bitiba), children: [jsx("div", { className: styles.Divider }), jsx(ButtonAnchor, { "data-zta": "addToCartPopoverCartLinkOverview", href: "/checkout/overview", length: "full", variant: `${!isAboveMinOrderValue || proceedToCheckoutFlag ? 'cart' : 'secondary'}`, onClick: () => sendAdobeData('goToBasket'), children: t('shop.add-to-cart.popover.checkout.overview') }), isAboveMinOrderValue && !proceedToCheckoutFlag && (jsx("a", { className: styles.CheckoutPreviewButton, href: "/checkout/preview", children: jsx(CartButton, { "aria-hidden": true, withIcon: true, "aria-labelledby": "cartButton", "data-zta": "addToCartPopoverCartLinkPreview", onClick: () => sendAdobeData('proceedToCheckout'), children: t('shop.add-to-cart.popover.checkout.preview') }) }))] })] }) }) }), document.body);
};

export { ARROW_HEIGHT, CLOSE_TIMEOUT, ErrorCodeTranslations, POPOVER_TITLE_ID, StatusTranslations, Popover as default };
//# sourceMappingURL=Popover.js.map
